<template>
  <b-modal
    id="edit-building-modal"
    title="Building Edit Modal"
    centered
    hide-footer
    size="lg"
    @hidden="reset"
    :no-close-on-esc="true"
    :no-close-on-backdrop="true"
  >
    <template #modal-title>
      <h2 class="m-0">Edit Building Details</h2>
    </template>
    <validation-observer ref="buildingEditFormValidation">
      <b-form @submit.prevent enctype="multipart/form-data">
        <b-form-group label-for="site">
          <template #label>
            Site <span class="text-danger">*</span>
          </template>
          <v-select
            label="name"
            v-model="site"
            placeholder="Select Site"
            :options="sites"
            autoscroll
            :clearable="true"
            :reduce="site => site.id"
            required
          ></v-select>
        </b-form-group>

        <b-form-group label-for="name">
          <template #label>
            Name <span class="text-danger">*</span>
          </template>
          <b-form-input
            id="name"
            v-model="name"
            v-restrict="regex"
            placeholder="Name"
            required
          />
        </b-form-group>

        <b-form-group label-for="building_type">
          <template #label>
            Building Type <span class="text-danger">*</span>
          </template>
          <v-select
            label="name"
            v-model="building_type"
            placeholder="Select Building Type"
            :options="building_types"
            autoscroll
            :clearable="true"
            required
          ></v-select>
        </b-form-group>

        <!-- Address Input Field -->
        <b-form-group label-for="address">
          <template #label>
            Address <span class="text-danger">*</span>
          </template>
          <b-form-input
            id="autocomplete-edit"
            v-model="address"
            placeholder="Enter your address"
            required
          />
        </b-form-group>

        <b-form-group label-for="ase_number">
          <template #label>
            ASE # <span class="text-danger">*</span>
          </template>
          <b-form-input
            id="ase_number"
            v-model="ase_number"
            placeholder="ASE #"
            type="number"
            required
          />
        </b-form-group>

        <b-form-group class="mt-2 text-right">
          <b-button
            type="submit"
            variant="primary"
            pill
            class="mr-1"
            @click="submit"
          >
            Save
          </b-button>
          <b-button
            type="reset"
            variant="outline-secondary"
            @click="reset"
            pill
          >
            Reset
          </b-button>
        </b-form-group>
      </b-form>
    </validation-observer>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "@validations";
import util from "@/util.js";
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";


export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    quillEditor
  },
  mixins: [util],
  props: {
    building: Object,
  },
  data() {
    return {
      required,
      name: "",
      building_type: "",
      address: "",
      latitude: null,
      longitude: null,
      regex: /^[a-zA-Z ]*$/,
      building_types: [
        {
          name: "Apartment",
          value: "Apartment"
        },
        {
          name: "Town House",
          value: "Town House"
        },
      ],
      site: "",
      sites: [],
      ase_number: "",
    };
  },
  async mounted() {
    const sites = await this.getSitesDDL();
    this.sites = sites.data;

    if (this.building) {
      this.name = this.building.name;
      this.site = this.building.site;
      this.building_type = this.building_types.find(
        type => type.value === this.building.building_type
      );
      this.address = this.building.address;
      this.latitude = this.building.latitude;
      this.longitude = this.building.longitude;
      this.ase_number = this.building.ase_number;
    }

    this.initAutocomplete();
  },
  methods: {
    ...mapActions({
      updateBuilding: "appData/updateBuilding",
      getSitesDDL: "appData/getSitesDDL",
    }),

    async validationForm() {
      const success = await this.$refs.buildingEditFormValidation.validate();
      if (success) {
        await this.submit();
      }
    },
    async submit() {
      try {
        const formData = new FormData();
        formData.append("name", this.name);
        formData.append("site", this.site);
        formData.append("building_type", this.building_type.value);
        formData.append("address", this.address);
        formData.append("latitude", this.latitude);
        formData.append("longitude", this.longitude);
        formData.append("ase_number", this.ase_number);
        formData.append('updated_by', this.getLoggedInUser.id); 
        const res = await this.updateBuilding({
          payload: formData,
          pk: this.building.id,
        });
        if (res.status === 200) {
          this.$toast(
            {
              component: ToastificationContent,
              props: {
                title: "Building Updated Successfully",
                variant: "success",
                icon: "BellIcon",
              },
            },
            {
              position: "top-right",
            }
          );
          this.reset();
          this.$nextTick(() => {
            this.$bvModal.hide("edit-building-modal");
          });
          this.$emit("modalClosed");
        }
      } catch (error) {
        this.displayError(error);
      }
    },

    reset() {
      this.name = "";
      this.address = "";
      this.building_type = "";
      this.latitude = null;
      this.longitude = null;
      this.site = "";
      this.ase_number = "";
    },

    initAutocomplete() {
      const input = document.getElementById('autocomplete-edit');
      const autocomplete = new google.maps.places.Autocomplete(input);

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place.geometry) {
          console.log('Returned place contains no geometry');
          return;
        }
        this.address = place.formatted_address;
        this.latitude = place.geometry.location.lat();
        this.longitude = place.geometry.location.lng();
      });
    }
  },
  computed: {
    ...mapGetters({ getLoggedInUser: "appData/getLoggedInUser" }),
  },
};
</script>

<style scoped>
.pac-container {
  z-index: 1051 !important; 
}
</style>
